// Call History-related filters

import {
	createDateRangeFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const callHistoryFilters: FilterRegistry = {
	/**
	 * PD-9630 — the headline keyword box on the Call History lists. Matches the
	 * phone number on the call (compared as digits, so any written form of the
	 * number finds it), the name captured on the call, or the name of the
	 * client/vendor the number is mapped to.
	 *
	 * Deliberately carries NO `fulltextFields`: none of those three things is a
	 * plain column on `call_user`, so the generic filter builder must emit
	 * nothing for this filter and `CallHistoryService` owns the WHERE (the
	 * filter id is listed in that builder's `SERVICE_APPLIED_FILTERS`). It is
	 * declared FIRST in each list's `activeFilters` because the list layer
	 * treats the first `text` filter as the list's search box.
	 */
	callHistorySearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Phone number or name',
			objectTable: 'call_user',
			listKeys: [
				'call_history',
				'call-history-client-embedded',
				'call-history-vendor-embedded',
			],
		}),
		queryParamName: 'callHistorySearch',
	},

	/**
	 * PD-9630 — narrow the Call History list to one or more customers. Resolved
	 * through `client_phone` (the same phone map that puts the client name on
	 * each row) rather than a column on `call_user`, so `CallHistoryService`
	 * applies it — hence `joinType: 'custom'` and the entry in
	 * `SERVICE_APPLIED_FILTERS`.
	 *
	 * Only on the main list: the embedded call-history tabs are already scoped
	 * to the client or vendor whose record is open.
	 */
	callHistoryClient: {
		type: 'multi-select',
		label: 'Customer',
		placeholder: 'Type to search customers...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: false,
		allowClear: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'callHistoryClient',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'call_user',
			fieldName: 'clientIds',
			joinType: 'custom',
		},
		listKeys: ['call_history'],
	},

	callHistoryDateRange: {
		...createDateRangeFilter({
			label: 'Date Range',
			objectTable: 'call_user',
			dateField: 'date',
			listKeys: [
				'call_history',
				'call-history-client-embedded',
				'call-history-vendor-embedded',
			],
		}),
		queryParamName: 'callHistoryDateRange',
		allowClear: true,
	},

	callHistoryUser: {
		type: 'multi-select',
		label: 'User',
		placeholder: 'Select users...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		queryParamName: 'callHistoryUser',
		apiEndpoint: '/users',
		multiple: true,
		options: [{ id: 'ME', label: 'Me' }],
		queryMapping: {
			objectTable: 'call_user',
			fieldName: 'fkUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [
			'call_history',
			'call-history-client-embedded',
			'call-history-vendor-embedded',
		],
	},

	callHistoryDirection: {
		...createRadioFilter({
			label: 'Direction',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'inbound', label: 'Inbound' },
				{ id: 'outbound', label: 'Outbound' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'call_user',
			fieldName: 'direction',
			listKeys: [
				'call_history',
				'call-history-client-embedded',
				'call-history-vendor-embedded',
			],
			colorScheme: 'purple',
		}),
		queryParamName: 'callHistoryDirection',
	},

	callHistoryInternalExternal: {
		...createRadioFilter({
			label: 'Internal/External',
			options: [
				{ id: 'All', label: 'All' },
				{ id: '0', label: 'External' },
				{ id: '1', label: 'Internal' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'call_user',
			fieldName: 'isInternal',
			listKeys: [
				'call_history',
				'call-history-client-embedded',
				'call-history-vendor-embedded',
			],
			colorScheme: 'orange',
		}),
		queryParamName: 'callHistoryInternalExternal',
	},
};
